<script setup lang="ts">
import { computed } from "vue"
import { useI18n } from "vue-i18n"

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from "@/shared/ui"
import { minutesOfDayInTz } from "../model/timeGrid"
import { useRecordsStore } from "../model/store"

const store = useRecordsStore()
const { t } = useI18n()

const open = computed(() => store.activeSheet === "deleteBlock")
const block = computed(() => store.editingBlock)

function formatHHMM(iso: string): string {
  const minOfDay = minutesOfDayInTz(iso, store.salonTz)
  const h = Math.floor(minOfDay / 60)
  const m = minOfDay % 60
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
}

const timeRange = computed(() => {
  if (!block.value) return ""
  return `${formatHHMM(block.value.startsAt)}–${formatHHMM(block.value.endsAt)}`
})

const reasonDisplay = computed(() => {
  if (!block.value) return ""
  return block.value.reason.length > 0 ? block.value.reason : t("journal.timeBlock.busy")
})

function handleCancel(): void {
  store.closeSheet()
}

async function handleConfirm(): Promise<void> {
  if (!block.value) return
  try {
    await store.submitDeleteBlock(block.value.id)
    store.closeSheet()
  } catch {
    // errors are handled by the store (toast); inline errors not expected for delete
  }
}
</script>

<template>
  <AlertDialog
    :open="open"
    @update:open="(v) => { if (!v) handleCancel() }"
  >
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ t("journal.timeBlock.deleteTitle") }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ timeRange }} · {{ reasonDisplay }}
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter>
        <AlertDialogCancel @click="handleCancel">
          {{ t("journal.timeBlock.cancelDelete") }}
        </AlertDialogCancel>
        <Button
          type="button"
          variant="destructive"
          :loading="store.blockMutationStatus === 'pending'"
          :disabled="store.blockMutationStatus === 'pending'"
          @click="handleConfirm"
        >
          {{ t("journal.timeBlock.confirmDelete") }}
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
